import {ContactModel} from '@app/models/contact.model';
import {AnswerGroupModuleCids} from '@app/models/enums/answer-group-module-cids.enum';
import {PhoneNumberFormatter} from '@app/shared/formatting.pipes';
import {BuilderFactory} from '@app/shared/utils/builder.factory';
import {CallCause, CallCauseUtil} from '@app/shared/utils/call-cause';
import {CallLogStringUtil} from '@app/shared/utils/call-log-string-util';
import {RedirectEntry} from '@app/shared/utils/redirect-entry';
import {CallLogEntry, EndUserCallLogEntry, SmsDto, SMSLogEntry} from '@gt-api';
import * as moment from 'moment';

export class CallLogEntryModel {
  phoneNumberFormatter = new PhoneNumberFormatter();
  redirectEntries: RedirectEntry[];
  id: number;
  callStart: number;
  cause: number;
  caller: string;
  fullNumber: string;
  aNumber: string;
  genericNumber: string;
  duration: number;
  trigger: number;
  accessNumber: string;
  answeredBy: string;
  company: string;
  login: string;
  cidName: string;
  handledBy: string;
  handledByName: string;

  static convertCallLogEntry(entry: CallLogEntry, answeredByName?: string, handledByName?: string): CallLogEntryModel {
    const model = BuilderFactory.createBuilder(CallLogEntryModel)
      .id(entry.id)
      .callStart(entry.start as any)
      .cause(entry.cause)
      .caller(CallLogStringUtil.getCallerDisplayName(entry))
      .fullNumber(entry.genericNumber || entry.aNumber)
      .aNumber(entry.aNumber)
      .genericNumber(entry.genericNumber)
      .duration(moment.duration(moment(entry.end).diff(entry.start)).asSeconds())
      .accessNumber(entry.serviceNumberName)
      .company(entry.company)
      .login(entry.login)
      .cidName(entry.cidName)
      .handledBy(entry.handledBy)
      .handledByName(handledByName || entry.handledBy)
      .redirectEntries(RedirectEntry.parse(entry.history))
      .build();

    /*
    Sometimes the call will be marked as answered even though it was not actually answered.
    For example, when a call goes from Queue to "No available agents announcement" then disconnect,
    it will be marked as answered in the Announcement module.
    */
    if (this.isCallMissedSwitchboard(entry) && CallCauseUtil.isCallAnswered(entry.cause)) {
      model.cause = CallCause.NoReply; // might need something else here
    } else {
      model.cause = entry.cause;
    }

    model.answeredBy = CallLogStringUtil.getAnsweredByDisplayName(model, answeredByName);
    return model;
  }

  private static isCallMissedSwitchboard(logEntry: CallLogEntry) {
    return (
      CallCauseUtil.isCallMissed(logEntry.cause) ||
      (!logEntry.login && !logEntry.history) ||
      (logEntry.cid !== AnswerGroupModuleCids.Queue && logEntry.cid !== AnswerGroupModuleCids.CallbackQueue && logEntry.cid !== AnswerGroupModuleCids.SmsNps)
    );
  }

  static convertEndUserCallLogEntry(entry: EndUserCallLogEntry): CallLogEntryModel {
    const name = entry.firstName ? entry.firstName + ' ' + entry.lastName : entry.lastName;
    return BuilderFactory.createBuilder(CallLogEntryModel)
      .id(-1)
      .callStart(entry.callStart as any)
      .cause(entry.cause)
      .caller(name)
      .fullNumber(entry.genericNumber || entry.peerNumber)
      .aNumber(entry.peerNumber)
      .genericNumber(entry.genericNumber)
      .duration(entry.duration)
      .trigger(entry.triggerPoint)
      .build();
  }

  isAnonymous(): boolean {
    return this.fullNumber === 'anonymous';
  }
}

export class SMSLogEntryModel {
  messageId?: number;
  to?: string;
  from?: string;
  message?: string;
  sentAt?: Date;
  reportAt?: Date;
  status?: string;
  statusCode?: number;
  statusType?: string;
  receiverModel?: ContactModel;

  static fromSMSLogEntry(logEntry: SMSLogEntry) {
    return BuilderFactory.createBuilder<SMSLogEntryModel>()
      .messageId(logEntry.messageId)
      .to(logEntry.to)
      .from(logEntry.from)
      .message(logEntry.message)
      .sentAt(logEntry.sentAt)
      .reportAt(logEntry.reportAt)
      .statusCode(logEntry.statusCode)
      .statusType(logEntry.statusType)
      .build();
  }
}

export class ConvertSmsDtoToSmsLogEntryModels {
  static convert(smsDto: SmsDto, date?: Date): SMSLogEntryModel[] {
    const models = [];
    smsDto.recipients.forEach(recipient => {
      const smsEntry = BuilderFactory.createBuilder<SMSLogEntryModel>()
        .to(recipient)
        .from(smsDto.sender)
        .sentAt(date ? date : new Date())
        .message(smsDto.message)
        .statusCode(0)
        .build();

      models.push(smsEntry);
    });

    return models;
  }
}
